import { z } from 'zod';
import { shortLabel } from './schema.js';
/**
 * Reglas de alerta sobre un indicador.
 *
 * Una regla cuelga de un indicador, así que se muda con él: el mismo indicador
 * puede tener un umbral en un tratamiento y otro distinto en otro, y eso sale
 * gratis porque el indicador ya es propio de cada tratamiento.
 *
 * La condición son campos y no un mini-lenguaje anidado. La pharma la arma desde
 * un formulario con desplegables, y "el promedio de los últimos 7 días es 5 o
 * más" es todo lo que ese formulario necesita expresar. Un DSL con and/or
 * obligaría a escribir un parser y a explicarle su sintaxis a alguien de
 * asuntos médicos que no programa.
 */
export const indicatorComparatorSchema = z.enum(['>', '>=', '<', '<=', '==', '!=']);
/**
 * Cómo se agrega el indicador antes de compararlo.
 *
 *   LATEST       último valor cargado
 *   MEAN/MAX/MIN sobre la ventana
 *   RATE         proporción 0-1 de mediciones que valen 1 — para booleanos
 *   DELTA_PCT    cambio porcentual contra el valor de referencia de la ventana
 *   DELTA_ABS    lo mismo, en valor absoluto
 *   MISSED_DAYS  ocurrencias esperadas por la frecuencia que no se cargaron
 */
export const indicatorAggregateSchema = z.enum([
    'LATEST',
    'MEAN',
    'MAX',
    'MIN',
    'RATE',
    'DELTA_PCT',
    'DELTA_ABS',
    'MISSED_DAYS',
]);
export const indicatorSeveritySchema = z.enum(['INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
/**
 * La forma base, sin la comprobación cruzada.
 *
 * Se exporta aparte porque `.refine` devuelve un `ZodEffects`, y sobre eso no se
 * puede pedir `.partial()` — que es justo lo que hace falta para editar una
 * regla mandando solo lo que se tocó.
 */
export const indicatorRuleFields = z
    .object({
    name: z.string().min(1).max(160),
    description: z.string().max(2000).nullish(),
    severity: indicatorSeveritySchema,
    aggregate: indicatorAggregateSchema.default('LATEST'),
    /** Ventana en días. Obligatoria para todo lo que no sea LATEST. */
    windowDays: z.number().int().positive().max(3650).nullish(),
    op: indicatorComparatorSchema,
    value: z.number(),
    message: z.string().min(1).max(500),
    /** Vacío = al paciente no se le muestra nada. */
    patientMessage: z.string().max(500).nullish(),
    notifyRoles: z.array(z.enum(['PHARMA_ADMIN', 'CASE_MANAGER'])).min(1).default(['CASE_MANAGER']),
    active: z.boolean().default(true),
    /**
     * No repetir para el mismo paciente durante N horas. Sin esto, un dolor
     * sostenido en 8 genera una alerta por día y el equipo deja de mirar la cola.
     */
    cooldownHours: z.number().int().min(0).max(8760).default(24),
});
export const indicatorRuleSchema = indicatorRuleFields.refine((rule) => rule.aggregate === 'LATEST' || Boolean(rule.windowDays), { message: 'Elegí sobre cuántos días se calcula', path: ['windowDays'] });
/** Días que se esperan entre una carga y la siguiente, según la frecuencia. */
const EXPECTED_EVERY = {
    DAILY: 1,
    WEEKLY: 7,
    BIWEEKLY: 14,
    MONTHLY: 30,
    ON_DEMAND: null,
    PER_DOSE: null,
};
const DAY_MS = 86_400_000;
/**
 * Evalúa una regla contra las mediciones de un paciente.
 *
 * Devuelve `matched: false` con un motivo cuando no hay datos suficientes, en
 * vez de disparar. Una alerta que salta porque el paciente todavía no cargó
 * nada es peor que no tener la alerta: enseña a ignorar la cola.
 */
export function evaluateIndicatorRule(rule, measurements, context) {
    const sorted = [...measurements].sort((a, b) => a.recordedAt.getTime() - b.recordedAt.getTime());
    const since = rule.windowDays
        ? new Date(context.now.getTime() - rule.windowDays * DAY_MS)
        : null;
    const inWindow = since ? sorted.filter((m) => m.recordedAt >= since) : sorted;
    const resolve = () => {
        switch (rule.aggregate) {
            case 'LATEST': {
                const last = sorted[sorted.length - 1];
                return last ? { value: last.value } : { value: null, reason: 'Todavía no cargó ninguna' };
            }
            case 'MEAN':
            case 'MAX':
            case 'MIN':
            case 'RATE': {
                if (inWindow.length === 0) {
                    return { value: null, reason: 'Sin mediciones en el período' };
                }
                const values = inWindow.map((m) => m.value);
                if (rule.aggregate === 'MAX')
                    return { value: Math.max(...values) };
                if (rule.aggregate === 'MIN')
                    return { value: Math.min(...values) };
                const sum = values.reduce((a, b) => a + b, 0);
                if (rule.aggregate === 'MEAN')
                    return { value: sum / values.length };
                // RATE: proporción de mediciones que valen 1.
                return { value: values.filter((v) => v === 1).length / values.length };
            }
            case 'DELTA_ABS':
            case 'DELTA_PCT': {
                // La referencia es la más vieja dentro de la ventana, no la anterior a
                // la ventana: si el paciente estuvo un mes sin cargar, comparar contra
                // un valor de hace tres meses no describe el período que se está
                // mirando.
                const first = inWindow[0];
                const last = inWindow[inWindow.length - 1];
                if (!first || !last || first === last) {
                    return { value: null, reason: 'Hacen falta al menos dos mediciones en el período' };
                }
                const delta = last.value - first.value;
                if (rule.aggregate === 'DELTA_ABS')
                    return { value: delta };
                if (first.value === 0)
                    return { value: null, reason: 'El valor de referencia es cero' };
                return { value: (delta / Math.abs(first.value)) * 100 };
            }
            case 'MISSED_DAYS': {
                const last = sorted[sorted.length - 1];
                if (!last)
                    return { value: null, reason: 'Todavía no cargó ninguna' };
                const elapsed = Math.floor((context.now.getTime() - last.recordedAt.getTime()) / DAY_MS);
                /**
                 * Días desde la última respuesta.
                 *
                 * Cuando el indicador declara una cadencia se le descuenta —esperar un
                 * dato semanal a los dos días no es un olvido—. Sin cadencia declarada
                 * se cuentan los días tal cual: antes devolvía `null` con un motivo, así
                 * que la regla no saltaba nunca y desde la pantalla parecía configurada.
                 */
                const every = EXPECTED_EVERY[context.frequency];
                return { value: Math.max(0, elapsed - (every ?? 0)) };
            }
        }
    };
    const { value, reason } = resolve();
    if (value === null)
        return { matched: false, resolved: null, reason };
    return { matched: compare(value, rule.op, rule.value), resolved: round(value) };
}
function compare(left, op, right) {
    switch (op) {
        case '>':
            return left > right;
        case '>=':
            return left >= right;
        case '<':
            return left < right;
        case '<=':
            return left <= right;
        case '==':
            return left === right;
        case '!=':
            return left !== right;
    }
}
function round(value) {
    return Math.round(value * 100) / 100;
}
// ── Cómo se lee una regla ────────────────────────────────────────────────────
const AGGREGATE_PHRASES = {
    LATEST: (label) => label,
    MEAN: (label, days) => `el promedio de ${label} en ${days} días`,
    MAX: (label, days) => `el valor más alto de ${label} en ${days} días`,
    MIN: (label, days) => `el valor más bajo de ${label} en ${days} días`,
    RATE: (label, days) => `el cumplimiento de ${label} en ${days} días`,
    DELTA_PCT: (label, days) => `el cambio porcentual de ${label} en ${days} días`,
    DELTA_ABS: (label, days) => `el cambio de ${label} en ${days} días`,
    MISSED_DAYS: (label) => `los días sin registrar ${label}`,
};
const COMPARATOR_PHRASES = {
    '>': (value) => `supera ${value}`,
    '>=': (value) => `es ${value} o más`,
    '<': (value) => `es menor que ${value}`,
    '<=': (value) => `es ${value} o menos`,
    '==': (value) => `es ${value}`,
    '!=': (value) => `es distinto de ${value}`,
};
/**
 * La regla en castellano, para que el equipo médico la lea sin traducir.
 *
 * Se arma acá y no en la pantalla porque el mismo texto lo necesitan la consola,
 * el detalle de la alerta y el correo que sale. Tres implementaciones del mismo
 * texto terminan diciendo tres cosas distintas.
 */
export function describeIndicatorRule(rule, indicator) {
    const label = shortLabel(indicator.label);
    const subject = AGGREGATE_PHRASES[rule.aggregate](label, rule.windowDays);
    const formatted = formatValue(rule.value, rule.aggregate, indicator.unit);
    const phrase = `${subject} ${COMPARATOR_PHRASES[rule.op](formatted)}`;
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}
function formatValue(value, aggregate, unit) {
    // Una proporción se lee como porcentaje: "adherencia de 0,8" obliga a
    // traducir mentalmente; "80%" se entiende de una.
    if (aggregate === 'RATE')
        return `${Math.round(value * 100)}%`;
    if (aggregate === 'DELTA_PCT')
        return `${value}%`;
    if (aggregate === 'MISSED_DAYS')
        return String(value);
    return unit ? `${value} ${unit}` : String(value);
}
//# sourceMappingURL=rules.js.map